import { authApiClient } from "./auth-client";
import { UserRole } from "@/types/ken-aqi-types/user";

export interface LoginRequest {
  email: string;
  password: string;
  deviceToken?: string; // Optional device token for 2FA purposes
}

export interface LoginResponse {
  token?: string;
  requires2FA?: boolean;
  tempToken?: string;
  hasSeenTwoFactorSetup?: boolean;
  deviceToken?: string; // Optional device token returned on successful 2FA login
}

export interface Verify2FALoginRequest {
  tempToken: string;
  code: string;
  trustDevice: boolean;
}

export interface Generate2FAResponse {
  qrCodeUrl: string;
  secret: string;
}

export interface CompleteSetupRequest {
  token: string;
  password: string;
  firstName: string;
  lastName: string;
}

/** POST /v1/auth/complete-setup — 200 returns session token */
export interface CompleteSetupResponse {
  token: string;
}

/** POST /v1/auth/admin/create-user — name fields may be empty strings when omitted */
export interface AdminCreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  organizationId: number;
  organizationRole: "USER" | "ADMIN";
  userType: UserRole;
}

export interface AdminCreateUserResponse {
  data: boolean;
}

/** POST /v1/auth/change-password — signed-in user, email/password accounts only */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {

    return authApiClient.post<LoginResponse>("/auth/login", credentials);
  },

  async completeSetup(
    data: CompleteSetupRequest
  ): Promise<CompleteSetupResponse> {
    return authApiClient.post<CompleteSetupResponse>(
      "/auth/complete-setup",
      data
    );
  },

  async adminCreateUser(
    data: AdminCreateUserRequest,
    token: string
  ): Promise<AdminCreateUserResponse> {
    return authApiClient.post<AdminCreateUserResponse>(
      "/auth/admin/create-user",
      data,
      token
    );
  },

  async changePassword(
    data: ChangePasswordRequest,
    token: string
  ): Promise<Record<string, unknown>> {
    return authApiClient.post<Record<string, unknown>>(
      "/auth/change-password",
      data,
      token
    );
  },

  async logout(token: string): Promise<{ data: boolean }> {
    return authApiClient.post<{ data: boolean }>("/auth/logout", {}, token);
  },

  // --- 2FA Methods ---
  async verify2FALogin(data: Verify2FALoginRequest): Promise<LoginResponse> {
    return authApiClient.post<LoginResponse>("/auth/login/2fa", data);
  },

  async generate2FA(token: string): Promise<Generate2FAResponse> {
    return authApiClient.post<Generate2FAResponse>("/auth/2fa/generate", {}, token);
  },

  async enable2FA(
    code: string, 
    token: string, 
    trustDevice: boolean
  ): Promise<{ data: boolean; deviceToken?: string }> {
    // Return the full JSON payload containing data and deviceToken status fields
    return authApiClient.post<{ data: boolean; deviceToken?: string }>(
      "/auth/2fa/enable", 
      { code, trustDevice }, 
      token
    );
  },

  async disable2FA(code: string, token: string, ): Promise<{ data: boolean }> {
    return authApiClient.post<{ data: boolean }>("/auth/2fa/disable", { code }, token);
  },

  async skip2FA(token: string): Promise<{ data: boolean }> {
    return authApiClient.post<{ data: boolean }>("/auth/2fa/skip", {}, token);
  },
};
